import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  InputAdornment, Alert, CircularProgress
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { INDESAP } from '../../theme';
import DevBadge from '../../components/common/DevBadge';

export default function Login() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.requestOTP(email.trim().toLowerCase());
      navigate('/verify-otp', { state: { email: email.trim().toLowerCase() } });
    } catch (err) {
      setError(err.response?.data?.error || 'Error al enviar el código. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2d2d2d 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative background circles */}
      <Box sx={{
        position: 'absolute', top: '-120px', right: '-120px',
        width: 400, height: 400, borderRadius: '50%',
        background: `radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)`
      }} />
      <Box sx={{
        position: 'absolute', bottom: '-80px', left: '-80px',
        width: 300, height: 300, borderRadius: '50%',
        background: `radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)`
      }} />

      <Box sx={{ m: 'auto', width: '100%', maxWidth: 440, px: 3 }}>
        {/* Logo area */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" sx={{
            color: 'white', fontWeight: 800, letterSpacing: '0.12em',
            textShadow: '0 2px 12px rgba(255,255,255,0.15)'
          }}>
            INDESAP
          </Typography>
          <Typography sx={{
            color: '#aaaaaa', letterSpacing: '0.22em', fontWeight: 300,
            fontSize: '0.75rem', mt: 0.5, textTransform: 'uppercase'
          }}>
            Gestor de Actividades
          </Typography>
        </Box>

        <Card sx={{
          borderRadius: 3,
          backdropFilter: 'blur(10px)',
          background: 'rgba(255,255,255,0.97)',
          boxShadow: `0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)`
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700, color: '#111111' }}>
              Iniciar Sesión
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ingresa tu correo para recibir un código de acceso
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Correo electrónico"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 3 }}
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || !email}
                sx={{ py: 1.5, fontSize: '1rem' }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Enviar Código de Acceso'}
              </Button>
            </form>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
              El código de verificación expirará en 10 minutos
            </Typography>
          </CardContent>
        </Card>

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 3, color: 'rgba(255,255,255,0.5)' }}>
          © {new Date().getFullYear()} INDESAP · Todos los derechos reservados
        </Typography>
      </Box>
      <DevBadge id="page:login" />
    </Box>
  );
}
